import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

import { SendingMediumV2Schema } from './use-load-invoice-sending-medium';

const URL = '/invoice_sending_medium';

const UpdateInvoiceSendingMediumVariablesSchema = z.object({
  sendingMedium: z.array(SendingMediumV2Schema),
});

export type UpdateInvoiceSendingMediumVariables = z.infer<
  typeof UpdateInvoiceSendingMediumVariablesSchema
>;

export async function updateInvoiceSendingMediumFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateInvoiceSendingMediumVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateInvoiceSendingMediumVariablesSchema.parse(variables));
}

export function useUpdateInvoiceSendingMedium() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateInvoiceSendingMediumVariables>({
    mutationFn: variables => updateInvoiceSendingMediumFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['invoiceSendingMedium'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateInvoiceSendingMediumVariables,
    'updateInvoiceSendingMedium'
  >('updateInvoiceSendingMedium', mutation);
}
